import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { FixWithAgentModal, buildAgentPrompt } from '../../../../components/optidev-cloud/FixWithAgentModal';
import { useProjectStore } from '../../../../stores/projectStore';
import { useAgentUIStore } from '../../../../stores/agentUIStore';
import { debuggingApi } from '../../../../lib/api/supabase';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../../components/ui/collapsible';
import { RefreshCw, Shield, Zap, ChevronDown, Copy, CheckCircle2, AlertTriangle, AlertCircle, Info, Bot } from 'lucide-react';
import toast from 'react-hot-toast';
import { SupabaseLintLevel } from '@optidev/shared-types';
import type { SupabaseLint, SupabaseAdvisorType } from '@optidev/shared-types';

interface AdvisorsTabProps {
  projectId: string;
}

export function AdvisorsTab({ projectId }: AdvisorsTabProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentProject = useProjectStore((state) => state.currentProject);
  const isAgentStreaming = useAgentUIStore((state) => state.isAgentStreaming);
  const [activeView, setActiveView] = useState<SupabaseAdvisorType>('security');
  const [securityLints, setSecurityLints] = useState<SupabaseLint[]>([]);
  const [performanceLints, setPerformanceLints] = useState<SupabaseLint[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [levelFilter, setLevelFilter] = useState<SupabaseLintLevel>(SupabaseLintLevel.ERROR);

  // Fix with Agent modal state
  const [fixModalOpen, setFixModalOpen] = useState(false);
  const [selectedLintForFix, setSelectedLintForFix] = useState<SupabaseLint | null>(null);

  useEffect(() => {
    if (token) {
      loadAdvisors();
    }
  }, [projectId, token]);

  const loadAdvisors = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [securityResponse, performanceResponse] = await Promise.all([
        debuggingApi.getAdvisors(token, projectId, 'security'),
        debuggingApi.getAdvisors(token, projectId, 'performance'),
      ]);
      setSecurityLints(securityResponse.lints || []);
      setPerformanceLints(performanceResponse.lints || []);
      setLastChecked(new Date());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load advisors: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!token) return;
    setRefreshing(true);
    try {
      await loadAdvisors();
      toast.success('Advisors refreshed');
    } catch (error) {
      // Error already handled in loadAdvisors
    } finally {
      setRefreshing(false);
    }
  };

  const toggleExpanded = (lintName: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(lintName)) {
      newExpanded.delete(lintName);
    } else {
      newExpanded.add(lintName);
    }
    setExpandedItems(newExpanded);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleOpenFixModal = (lint: SupabaseLint) => {
    setSelectedLintForFix(lint);
    setFixModalOpen(true);
  };

  const handleSendToAgent = () => {
    if (!selectedLintForFix) return;

    // Build the prompt
    const prompt = buildAgentPrompt(selectedLintForFix, activeView);

    // Save to localStorage for AgentModeContainer to pick up
    localStorage.setItem('pendingAgentPrompt', JSON.stringify({
      prompt,
      autoSend: true,
      timestamp: Date.now(),
    }));

    // Close modal
    setFixModalOpen(false);
    setSelectedLintForFix(null);

    // Navigate to Power Editor - workspaceId from store or URL params
    const workspaceId = currentProject?.workspaceId || searchParams.get('ws');
    const url = `/dashboard/editor/${projectId}/power${workspaceId ? `?ws=${workspaceId}` : ''}`;
    navigate(url);
  };

  const getLevelIcon = (level: SupabaseLintLevel) => {
    switch (level) {
      case 'ERROR':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'WARN':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'INFO':
        return <Info className="h-4 w-4 text-primary" />;
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getLevelBadgeClass = (level: SupabaseLintLevel) => {
    switch (level) {
      case 'ERROR':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'WARN':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'INFO':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const allCurrentLints = activeView === 'security' ? securityLints : performanceLints;
  const currentLints = allCurrentLints.filter(lint => lint.level === levelFilter);

  const countByLevel = (lints: SupabaseLint[]) => {
    return {
      error: lints.filter(l => l.level === 'ERROR').length,
      warn: lints.filter(l => l.level === 'WARN').length,
      info: lints.filter(l => l.level === 'INFO').length,
    };
  };

  const counts = countByLevel(allCurrentLints);

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground">Loading advisors...</div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", isMobile && "space-y-3")}>
      {/* Header - compact on mobile */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Advisors</h3>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 hover:bg-muted rounded-md transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">Advisors</h3>
            <p className="text-sm text-muted-foreground">
              Check your database for security vulnerabilities and performance issues
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastChecked && (
              <span className="text-sm text-muted-foreground">
                Last checked: {formatTimeAgo(lastChecked)}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      )}

      {/* Filter Bar - horizontal scroll on mobile */}
      {isMobile ? (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
          {/* Advisor Type Toggle */}
          <div className="flex rounded-lg border border-border/60 overflow-hidden flex-shrink-0">
            <button
              onClick={() => setActiveView('security')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                activeView === 'security'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground'
              }`}
            >
              <Shield className="h-3 w-3" />
              {securityLints.length}
            </button>
            <button
              onClick={() => setActiveView('performance')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border-l border-border/60 transition-colors ${
                activeView === 'performance'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground'
              }`}
            >
              <Zap className="h-3 w-3" />
              {performanceLints.length}
            </button>
          </div>

          {/* Level Filter Pills */}
          <button
            onClick={() => setLevelFilter(SupabaseLintLevel.ERROR)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${
              levelFilter === SupabaseLintLevel.ERROR
                ? 'bg-destructive/10 text-destructive'
                : 'text-muted-foreground'
            }`}
          >
            <AlertCircle className="h-3 w-3" />
            {counts.error}
          </button>
          <button
            onClick={() => setLevelFilter(SupabaseLintLevel.WARN)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${
              levelFilter === SupabaseLintLevel.WARN
                ? 'bg-amber-500/10 text-amber-500'
                : 'text-muted-foreground'
            }`}
          >
            <AlertTriangle className="h-3 w-3" />
            {counts.warn}
          </button>
          <button
            onClick={() => setLevelFilter(SupabaseLintLevel.INFO)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${
              levelFilter === SupabaseLintLevel.INFO
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground'
            }`}
          >
            <Info className="h-3 w-3" />
            {counts.info}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          {/* Advisor Type Toggle */}
          <div className="flex rounded-lg border border-border/60 overflow-hidden">
            <button
              onClick={() => setActiveView('security')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                activeView === 'security'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Shield className="h-3.5 w-3.5" />
              Security
              {securityLints.length > 0 && (
                <span className={`ml-0.5 tabular-nums ${activeView === 'security' ? 'text-background/70' : 'text-muted-foreground'}`}>
                  {securityLints.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveView('performance')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-border/60 transition-colors ${
                activeView === 'performance'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Zap className="h-3.5 w-3.5" />
              Performance
              {performanceLints.length > 0 && (
                <span className={`ml-0.5 tabular-nums ${activeView === 'performance' ? 'text-background/70' : 'text-muted-foreground'}`}>
                  {performanceLints.length}
                </span>
              )}
            </button>
          </div>

          <div className="h-4 w-px bg-border/60" />

          {/* Level Filter Pills */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setLevelFilter(SupabaseLintLevel.ERROR)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                levelFilter === SupabaseLintLevel.ERROR
                  ? 'bg-destructive/10 text-destructive'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <AlertCircle className="h-3 w-3" />
              Errors
              <span className="tabular-nums">{counts.error}</span>
            </button>
            <button
              onClick={() => setLevelFilter(SupabaseLintLevel.WARN)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                levelFilter === SupabaseLintLevel.WARN
                  ? 'bg-amber-500/10 text-amber-500'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <AlertTriangle className="h-3 w-3" />
              Warnings
              <span className="tabular-nums">{counts.warn}</span>
            </button>
            <button
              onClick={() => setLevelFilter(SupabaseLintLevel.INFO)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                levelFilter === SupabaseLintLevel.INFO
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Info className="h-3 w-3" />
              Info
              <span className="tabular-nums">{counts.info}</span>
            </button>
          </div>
        </div>
      )}

      {/* Issues List */}
      <div className="space-y-3">
        {currentLints.length === 0 ? (
          <div className="rounded-lg border border-border/60 p-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
            <h3 className="text-base font-semibold mb-1.5">
              No {levelFilter.toLowerCase()} level issues found!
            </h3>
            <p className="text-sm text-muted-foreground">
              Your database {activeView === 'security' ? 'security' : 'performance'} configuration looks good for this severity level.
            </p>
          </div>
        ) : (
          currentLints.map((lint) => (
            <Collapsible
              key={lint.name}
              open={expandedItems.has(lint.name)}
              onOpenChange={() => toggleExpanded(lint.name)}
            >
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <CollapsibleTrigger className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                  {getLevelIcon(lint.level)}
                  <Badge variant="outline" className={getLevelBadgeClass(lint.level)}>
                    {lint.level}
                  </Badge>
                  <span className="font-mono text-sm text-muted-foreground">
                    {lint.name}
                  </span>
                  <span className="flex-1 text-left font-medium truncate">
                    {lint.title}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      expandedItems.has(lint.name) ? 'rotate-180' : ''
                    }`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-2 border-t border-border/40 space-y-3">
                    {lint.description && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-1">
                          Description
                        </h4>
                        <p className="text-sm">{lint.description}</p>
                      </div>
                    )}
                    {lint.detail && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-1">
                          Details
                        </h4>
                        <p className="text-sm font-mono bg-muted p-2 rounded break-words overflow-hidden">
                          {lint.detail}
                        </p>
                      </div>
                    )}
                    {lint.metadata && (lint.metadata.schema || lint.metadata.name) && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-1">
                          Affected Object
                        </h4>
                        <p className="text-sm font-mono">
                          {lint.metadata.schema && `${lint.metadata.schema}.`}
                          {lint.metadata.name}
                          {lint.metadata.type && ` (${lint.metadata.type})`}
                        </p>
                      </div>
                    )}
                    {lint.remediation && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-sm font-semibold text-muted-foreground">
                            Remediation
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(lint.remediation)}
                            className="h-7 px-2"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <pre className="text-sm font-mono bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap break-words">
                          {lint.remediation}
                        </pre>
                      </div>
                    )}
                    {/* Action buttons */}
                    <div className="pt-2 border-t border-border/40 flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isAgentStreaming}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFixModal(lint);
                        }}
                        className="text-primary hover:text-primary disabled:opacity-50"
                        title={isAgentStreaming ? "Agent is currently processing a request" : undefined}
                      >
                        <Bot className="h-4 w-4 mr-2" />
                        {isAgentStreaming ? "Agent Busy" : "Fix with Agent"}
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))
        )}
      </div>

      {/* Fix with Agent Modal */}
      <FixWithAgentModal
        isOpen={fixModalOpen}
        onClose={() => {
          setFixModalOpen(false);
          setSelectedLintForFix(null);
        }}
        onSend={handleSendToAgent}
        lint={selectedLintForFix}
        advisorType={activeView}
      />
    </div>
  );
}
